import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { X, Shield } from 'lucide-react';
import { initAnalyticsIfConsented } from '../lib/analytics';
import { uiL } from '../lib/localeDisplay';

const STORAGE_KEY = 'bienlivrer_cookie_consent';

type ConsentState = 'pending' | 'accepted' | 'declined';

export default function CookieBanner() {
  const { i18n, t } = useTranslation();
  const { pathname } = useLocation();
  const L = <T,>(fr: T, de: T, en: T, it: T) => uiL(i18n.language, fr, de, en, it);
  const [consent, setConsent] = useState<ConsentState>('pending');
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'accepted' || stored === 'declined') {
      setConsent(stored);
      if (stored === 'accepted') initAnalyticsIfConsented();
    } else {
      // Montrer le banner après 500ms (meilleure UX)
      const timer = setTimeout(() => setVisible(true), 500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem(STORAGE_KEY, 'accepted');
    setConsent('accepted');
    setVisible(false);
    initAnalyticsIfConsented();
  };

  const handleDecline = () => {
    localStorage.setItem(STORAGE_KEY, 'declined');
    setConsent('declined');
    setVisible(false);
  };

  if (!visible || consent !== 'pending') return null;

  return (
    <div className={`fixed left-0 right-0 z-50 p-3 sm:p-4 animate-slide-up ${
      pathname === '/landing'
        ? 'bottom-[5.75rem] md:bottom-0 md:pb-[max(0.75rem,env(safe-area-inset-bottom))] pb-3'
        : 'bottom-0 pb-[max(0.75rem,env(safe-area-inset-bottom))]'
    }`}>
      <div className="max-w-4xl mx-auto bg-white border border-gray-200 rounded-2xl shadow-2xl p-4 sm:p-5">
        <div className="flex items-start gap-3 sm:gap-4">
          <div className="w-10 h-10 bg-primary-50 rounded-xl flex items-center justify-center shrink-0">
            <Shield size={20} className="text-primary-700" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900 text-sm mb-1">
              {L('🍪 Cookies & confidentialité', '🍪 Cookies & Datenschutz', '🍪 Cookies & privacy', '🍪 Cookie e privacy')}
            </p>
            <p className="text-gray-500 text-sm leading-relaxed">
              {L(
                'Nous utilisons des cookies essentiels pour le fonctionnement du service et des cookies analytiques pour améliorer votre expérience. Conformément au RGPD et à la nLPD suisse.',
                'Wir verwenden wesentliche Cookies für den Betrieb des Dienstes und analytische Cookies zur Verbesserung Ihrer Erfahrung. Gemäß DSGVO und Schweizer nDSG.',
                'We use essential cookies to run the service and analytics cookies to improve your experience, in line with GDPR and the Swiss FADP.',
                'Usiamo cookie essenziali per il funzionamento del servizio e cookie analitici per migliorare l’esperienza, in conformità al GDPR e alla LPD svizzera.',
              )}
              {' '}
              <Link to="/privacy" className="text-primary-700 hover:underline font-medium">
                {L('En savoir plus', 'Mehr erfahren', 'Learn more', 'Per saperne di più')}
              </Link>
            </p>
          </div>
          <button
            type="button"
            onClick={handleDecline}
            className="p-1.5 min-w-11 min-h-11 flex items-center justify-center hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors shrink-0"
            aria-label={t('gdpr.closeBanner')}
          >
            <X size={16} aria-hidden />
          </button>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-4">
          <button
            onClick={handleAccept}
            className="w-full sm:w-auto sm:flex-none bg-primary-700 hover:bg-primary-800 text-white font-semibold px-5 py-2.5 rounded-xl text-sm transition-colors min-h-11">
            {L('Accepter tout', 'Alle akzeptieren', 'Accept all', 'Accetta tutto')}
          </button>
          <button
            onClick={handleDecline}
            className="w-full sm:w-auto sm:flex-none bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium px-5 py-2.5 rounded-xl text-sm transition-colors min-h-11">
            {L('Essentiels uniquement', 'Nur Wesentliche', 'Essential only', 'Solo essenziali')}
          </button>
        </div>
      </div>
    </div>
  );
}
